import { types, flow, Instance, applySnapshot } from 'mobx-state-tree';

import { StepType } from './types/step.types';

import { getSteps } from './apiWrappers/wrappers/useStep';
import { rearrangeSteps } from '../utils/utilsFunctions';

export interface StepProps {
  id: string;
  type: StepType;
  stepType: StepType;
  title: string;
  description: string;
  failStepId: string;
  nextStepId: string;
  ctaButtonTitle: string;
  metaData: {
    [key: string]: any;
  };
  analytics: string;
  canGoBack: boolean;
  canSkip: boolean;
  previousStepId?: string;
}

interface StepsDocument {
  id: string;
  title: string;
  origin?: string;
  criteriaOrigin?: string;
  isCriteriasPreLoaded?: boolean;
  isLabelsPreLoaded?: boolean;
  enableProgram?: boolean;
  isReDiscovery?: boolean;
  steps: StepProps[];
  applyCoupon: string;
}

const StepModel = types.model('StepModel', {
  // for react step wizard, this is the ID
  stepName: types.identifier,
  stepType: types.enumeration([...Object.values(StepType)]),
  title: types.maybeNull(types.string),
  description: types.maybeNull(types.string),
  failStepId: types.maybeNull(types.string),
  nextStepId: types.maybeNull(types.string),
  ctaButtonTitle: types.maybeNull(types.string),
  metaData: types.maybeNull(types.frozen()),
  analytics: types.maybeNull(types.frozen()),
  canGoBack: types.optional(types.boolean, false),
  canSkip: types.optional(types.boolean, false),
  previousStepId: types.maybeNull(types.string),
});

interface Step extends Instance<typeof StepModel> {}

const StepStoreModel = types
  .model('StepStoreModel', {
    id: types.maybeNull(types.string),
    title: types.maybeNull(types.string),
    origin: types.maybeNull(types.string),
    criteriaOrigin: types.maybeNull(types.string),
    isCriteriasPreLoaded: types.optional(types.boolean, false),
    isLabelsPreLoaded: types.optional(types.boolean, false),
    enableProgram: types.optional(types.boolean, false),
    isReDiscovery: types.optional(types.boolean, false),
    applyCoupon: types.optional(types.string, ''),
    steps: types.array(StepModel),
  })
  .actions((self) => ({
    getSteps: flow(function* fetchData(id: string) {
      const { kind, data } = yield getSteps(id);
      if (kind === 'ok') {
        const stepsDocument: StepsDocument = JSON.parse(data.documentContent);

        const {
          id,
          title,
          steps,
          origin,
          criteriaOrigin,
          isCriteriasPreLoaded = false,
          isLabelsPreLoaded = false,
          enableProgram = false,
          applyCoupon,
          isReDiscovery = false,
        } = stepsDocument;
        const newSteps = rearrangeSteps(steps);

        applySnapshot(self, {
          ...self,
          id,
          title,
          origin,
          criteriaOrigin,
          isCriteriasPreLoaded,
          isLabelsPreLoaded,
          enableProgram,
          isReDiscovery,
          steps: newSteps,
          applyCoupon,
        });
      }
    }),
  }));

export { StepModel, StepStoreModel, Step };
